import { useEffect, useRef, useState } from 'react';
import { X as CloseIcon } from 'lucide-react';

import { TranslationsKeys } from 'types/translation';
import { Survey, SurveyOption, SurveyQuestion } from 'types/survey';
import { Answer } from 'types/surveyResponse';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { useTranslate } from 'hooks/useTranslate';
import { filterDependent } from 'utils/survey';
import { FullscreenContainer } from 'components/FullscreenContainer';
import { SearchInput } from 'components/ui/SearchInput';
import styles from './DropdownQuestion.module.scss';

interface IProps {
  surveyDefinition: Survey;
  question: SurveyQuestion;
  answer: Answer;
  siblingAnswers: Answer[];
  onChange: (answer: Answer) => void;
}

export const DropdownQuestion = ({ surveyDefinition, question, answer, siblingAnswers, onChange }: IProps) => {
  const { t } = useTranslate();
  const ts = useSurveyTranslation(surveyDefinition);
  const [active, setActive] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<SurveyOption[]>([...question.options!]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // reset options on dropdown close
    if (!active) {
      setFilteredOptions([...question.options!]);
    }
  }, [active]);
  
  const filterOptions = (value: string) => {
      value = value.trim().toLowerCase();
      if (!value.length) {
        setFilteredOptions([...question.options!]);
      } else {
        setFilteredOptions(question.options!.filter(o => ts(o.name).toLowerCase().includes(value)));
      }
  };
  
  return <>
    <button type="button" className={styles.dropdownQuestion} onClick={() => setActive(true)}>
      <span>{ts(question.name)}:</span>
      <span className={styles.dropdownQuestionValue}>{question.options?.find(o => o.value === answer.value)?.name ?? ''}</span>
    </button>
    {active && <FullscreenContainer>
      <div className={styles.dropdownQuestionSearch}>
        <div className={styles.dropdownQuestionSearchHeading}>
          <span>{ts(question.name)}</span>
          <button onClick={() => setActive(false)}>
              <CloseIcon/>
          </button>
        </div>
        <div className={styles.dropdownQuestionSearchForm}>
          <SearchInput 
              variant="inline"
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setActive(false);
                }
              }}
              onChange={filterOptions} 
              inputRef={searchInputRef} />
          <div className={styles.dropdownQuestionSearchFormResultCount}>
            ({filteredOptions.length}) {t(TranslationsKeys.common_results).toLowerCase()}
          </div>
        </div>
        <div className={styles.dropdownQuestionSearchResults}>
          {filteredOptions
            ?.filter(o => filterDependent(o, siblingAnswers))
            .map((o) => <button 
              key={o.value}
              type="button"
              className={styles.dropdownQuestionSearchResult}
              onClick={() => {
                setActive(false);
                onChange({ ...answer, value: o.value });
              }}
          >{ts(o.name)}</button>)}
        </div>
      </div>
    </FullscreenContainer>}
  </>;
};
